import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Plus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

const STATUS_CLS = {
  aperta: "bg-red-100 text-red-800 border-red-200",
  in_gestione: "bg-amber-100 text-amber-800 border-amber-200",
  risolta: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const CATEGORIES = ["temperatura", "haccp", "igiene", "infestanti", "fornitore", "attrezzatura", "altro"];

export default function NonConformitiesPage() {
  const { locationId } = useAuth();
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ category: "altro", problem: "", corrective_action: "", assigned_to: "", notes: "" });
  const [edit, setEdit] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/nonconformities", { params: { location_id: locationId, status: filter || undefined } })
      .then((r) => setItems(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [locationId, filter]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/nonconformities", { ...form, location_id: locationId });
      toast.success(t("common.success"));
      setDialog(false);
      setForm({ category: "altro", problem: "", corrective_action: "", assigned_to: "", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const patch = async (nc, updates) => {
    try {
      await api.patch(`/nonconformities/${nc.nc_id}`, updates);
      toast.success(t("common.success"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5 md:pl-14" data-testid="nonconformities-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("nc.title")}</h1>
        <Button data-testid="new-nc-button" onClick={() => setDialog(true)} className="h-12 tactile-btn font-bold">
          <Plus className="h-5 w-5 mr-1" />{t("nc.new")}
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="nc-filters">
        {["", "aperta", "in_gestione", "risolta"].map((s) => (
          <button key={s} data-testid={`nc-filter-${s || "all"}`} onClick={() => setFilter(s)}
            className={`tactile-btn h-10 px-4 rounded-xl border text-sm font-semibold ${filter === s ? "bg-primary text-white border-primary" : "bg-card"}`}>
            {s ? t(`nc.${s}`) : "Tutte / All"}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="nc-list">
        {items.map((nc) => (
          <div key={nc.nc_id} data-testid={`nc-card-${nc.nc_id}`} className="bg-card border rounded-2xl p-4 space-y-3">
            <div className="flex items-start gap-3 flex-wrap">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 ${nc.status === "risolta" ? "bg-emerald-600" : "bg-red-600"}`}>
                <AlertTriangle className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-48">
                <p className="font-semibold text-sm">{nc.problem}</p>
                <p className="text-xs text-muted-foreground">
                  {nc.category} · {nc.user_name} · {format(new Date(nc.created_at), "dd/MM/yyyy HH:mm")}
                </p>
              </div>
              <span data-testid={`nc-status-${nc.nc_id}`} className={`text-[10px] font-bold uppercase px-2.5 py-1.5 rounded-lg border ${STATUS_CLS[nc.status]}`}>
                {t(`nc.${nc.status}`)}
              </span>
              <button data-testid={`nc-delete-${nc.nc_id}`} title={t("common.delete")}
                onClick={async () => {
                  if (!window.confirm(t("common.confirmDelete"))) return;
                  try { await api.delete(`/nonconformities/${nc.nc_id}`); toast.success(t("common.success")); load(); }
                  catch (e) { toast.error(formatApiError(e)); }
                }}
                className="tactile-btn h-9 w-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-600 shrink-0">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            {nc.status !== "risolta" && (
              <div className="flex gap-2 flex-wrap items-center">
                <Input data-testid={`nc-corrective-${nc.nc_id}`} placeholder={t("nc.corrective")}
                  value={edit[nc.nc_id] ?? nc.corrective_action ?? ""}
                  onChange={(e) => setEdit({ ...edit, [nc.nc_id]: e.target.value })}
                  className="h-11 flex-1 min-w-48" />
                {nc.status === "aperta" && (
                  <Button data-testid={`nc-progress-${nc.nc_id}`} variant="outline" className="h-11 tactile-btn"
                    onClick={() => patch(nc, { status: "in_gestione", corrective_action: edit[nc.nc_id] ?? nc.corrective_action })}>
                    {t("nc.markProgress")}
                  </Button>
                )}
                <Button data-testid={`nc-resolve-${nc.nc_id}`}
                  className="h-11 bg-emerald-600 hover:bg-emerald-700 tactile-btn"
                  onClick={() => patch(nc, { status: "risolta", corrective_action: edit[nc.nc_id] ?? nc.corrective_action })}>
                  {t("nc.markResolved")}
                </Button>
              </div>
            )}
            {nc.status === "risolta" && nc.corrective_action && (
              <p className="text-xs text-muted-foreground bg-secondary rounded-lg px-3 py-2">
                {t("nc.corrective")}: {nc.corrective_action}
              </p>
            )}
          </div>
        ))}
        {!items.length && (
          <div className="text-center py-16 text-muted-foreground text-sm" data-testid="nc-empty">
            <AlertTriangle className="h-10 w-10 mx-auto mb-2 opacity-40" />
            {t("notif.empty")}
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent className="max-w-sm" data-testid="nc-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("nc.new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>{t("nc.category")}</Label>
              <select data-testid="nc-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-11 w-full rounded-md border bg-background px-3 text-sm">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t("nc.problem")}</Label>
              <Textarea data-testid="nc-form-problem" value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} rows={3} />
            </div>
            <div className="space-y-1">
              <Label>{t("nc.corrective")}</Label>
              <Input data-testid="nc-form-corrective" value={form.corrective_action} onChange={(e) => setForm({ ...form, corrective_action: e.target.value })} className="h-11" />
            </div>
            <div className="space-y-1">
              <Label>{t("nc.assigned")}</Label>
              <Input data-testid="nc-form-assigned" value={form.assigned_to} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} className="h-11" />
            </div>
            <Button data-testid="nc-form-save" onClick={create} disabled={busy || !form.problem} className="w-full h-12 tactile-btn font-bold">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("common.save")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
